import React, { useCallback, useMemo, useState } from 'react';
import { Card, Form, Alert } from 'react-bootstrap';
import { DataTable } from './common/DataTable';
import type { ColumnConfig } from './common/DataTable/types';
import {
  getReportsIncomeBreakdown,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import {
  GetReportsIncomeBreakdown200,
  GetReportsIncomeBreakdownParams,
  GetReportsIncomeBreakdownParamsBreakdownBy,
  GetReportsIncomeBreakdownParamsSource,
  IncomeBreakdownRow,
  IncomeBreakdownSummary,
} from '../api/generated/model';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import type { FinancialReportsFiltersState } from './FinancialReportsFilterBar';

export type BreakdownDimensionKey = 'state' | 'program_type' | 'program' | 'customer' | 'item';

interface IncomeBreakdownSectionProps {
  dateRange: { start_date?: string; end_date?: string };
  filters: FinancialReportsFiltersState;
  // Owned by the page so the URL params can persist the selected
  // dimension across reloads / deep-links. Section is purely a view
  // over (state, onStateChange).
  dimension: BreakdownDimensionKey;
  onDimensionChange: (d: BreakdownDimensionKey) => void;
  // Which row-source the backend should aggregate over. Defaults to
  // 'events' for the Program Metrics tab; pass 'non_program_lines'
  // for the Non Program Metrics tab.
  source?: GetReportsIncomeBreakdownParamsSource;
  // Allowed dimensions in the dropdown — narrows the global list per
  // source (e.g. non_program excludes program_type / program / state).
  // When omitted, all dimensions show.
  allowedDimensions?: BreakdownDimensionKey[];
  // Label for the row-count column. Defaults to "Events"; the
  // non-program tab should pass "Lines" since each row is one
  // invoice line, not one event.
  countLabel?: string;
  // Rendered INSIDE the section's Card.Header above the dimension
  // dropdown. Used by FinancialReportsPage to put the (shared) date
  // nav + filter bar in the SAME card as the DataTable — necessary
  // for the FilterControls Popper `sizeMenuToCard` modifier to find
  // a big enough .card to size dropdown menus against.
  headerPrefix?: React.ReactNode;
  userId?: string;
  userPreferences?: any;
}

interface DimensionOption {
  value: BreakdownDimensionKey;
  label: string;
  enumValue: GetReportsIncomeBreakdownParamsBreakdownBy;
  labelColHeader: string;
}

const DIMENSIONS: DimensionOption[] = [
  { value: 'state', label: 'State', enumValue: GetReportsIncomeBreakdownParamsBreakdownBy.GetReportsIncomeBreakdownParamsBreakdownByState, labelColHeader: 'State' },
  { value: 'program_type', label: 'Activity Type', enumValue: GetReportsIncomeBreakdownParamsBreakdownBy.GetReportsIncomeBreakdownParamsBreakdownByProgramType, labelColHeader: 'Activity Type' },
  { value: 'program', label: 'Program', enumValue: GetReportsIncomeBreakdownParamsBreakdownBy.GetReportsIncomeBreakdownParamsBreakdownByProgram, labelColHeader: 'Program' },
  { value: 'customer', label: 'Customer', enumValue: GetReportsIncomeBreakdownParamsBreakdownBy.GetReportsIncomeBreakdownParamsBreakdownByCustomer, labelColHeader: 'Customer' },
  { value: 'item', label: 'Item', enumValue: GetReportsIncomeBreakdownParamsBreakdownBy.GetReportsIncomeBreakdownParamsBreakdownByItem, labelColHeader: 'Item' },
];

const FETCH_LIMIT = 100;

const formatCurrencyFull = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

// IncomeBreakdownSection — events + revenue grouped by one dimension.
// Reuses the page's date range + filter set. Cursor-paginated through
// usePaginatedQuery (state has ~38 rows so a single page is the common
// case; customer/program breakdowns can span multiple pages).
const IncomeBreakdownSection: React.FC<IncomeBreakdownSectionProps> = ({
  dateRange,
  filters,
  dimension,
  onDimensionChange,
  source,
  allowedDimensions,
  countLabel,
  headerPrefix,
  userId,
  userPreferences,
}) => {
  const dimensions = useMemo(
    () => allowedDimensions
      ? DIMENSIONS.filter(d => allowedDimensions.includes(d.value))
      : DIMENSIONS,
    [allowedDimensions],
  );
  const dimensionOpt = useMemo(
    () => dimensions.find(d => d.value === dimension) || dimensions[0],
    [dimension, dimensions],
  );

  const [sortField, setSortField] = useState<'label' | 'event_count' | 'revenue' | 'percent_of_total'>('revenue');
  const [sortAsc, setSortAsc] = useState(false);

  // buildQueryParams contract (per usePaginatedQuery):
  //   - cursor present  → return { cursor, limit }
  //   - cursor absent   → return full first-page param set
  // useCallback is required so the hook's dep-tracking re-resets on
  // filter/sort/date changes (those are part of the deps below).
  const buildQueryParams = useCallback(
    (cursor: string | undefined) => {
      if (cursor) return { cursor, limit: FETCH_LIMIT };
      const params: any = {
        breakdown_by: dimensionOpt.enumValue,
        sort_field: sortField,
        sort_direction: sortAsc ? 'asc' : 'desc',
        limit: FETCH_LIMIT,
      };
      if (source) params.source = source;
      if (dateRange.start_date) params.start_date = dateRange.start_date;
      if (dateRange.end_date) params.end_date = dateRange.end_date;
      if (filters.item_ids) params.item_ids = filters.item_ids;
      if (filters.program_names) params.program_names = filters.program_names;
      if (filters.customer_ids) params.customer_ids = filters.customer_ids;
      if (filters.customer_name) params.customer_name = filters.customer_name;
      if (filters.salesperson_names) params.salesperson_names = filters.salesperson_names;
      if (filters.sales_lead_names) params.sales_lead_names = filters.sales_lead_names;
      if (filters.state_codes) params.state_codes = filters.state_codes;
      return params;
    },
    [dateRange, filters, dimensionOpt.enumValue, source, sortField, sortAsc],
  );

  const {
    allItems: rows,
    apiSummary: summary,
    isLoading,
    hasMore,
    handleLoadMore,
    error,
  } = usePaginatedQuery<IncomeBreakdownRow, GetReportsIncomeBreakdown200, IncomeBreakdownSummary>({
    // queryKey scoped by source as well as dimension so the events-tab
    // and non-program-tab don't collide on the same dimension (e.g.
    // both have a "customer" breakdown but they query disjoint rows).
    queryKey: `income-breakdown-${source ?? 'events'}-${dimension}`,
    buildQueryParams,
    queryFn: (params) => getReportsIncomeBreakdown(params as GetReportsIncomeBreakdownParams),
    getRowKey: (r) => r.label || '',
  });

  const columns: ColumnConfig<IncomeBreakdownRow>[] = [
    {
      key: 'label',
      label: dimensionOpt.labelColHeader,
      width: 240,
      sortable: true,
      renderCell: (r) => <span>{r.label || '(unknown)'}</span>,
    },
    {
      key: 'event_count',
      label: countLabel ?? 'Events',
      width: 100,
      sortable: true,
      renderCell: (r) => (
        <span className="d-block text-end" style={{ fontFamily: 'monospace' }}>
          {r.event_count ?? 0}
        </span>
      ),
    },
    {
      key: 'revenue',
      label: 'Revenue',
      width: 140,
      sortable: true,
      renderCell: (r) => (
        <span className="d-block text-end text-success" style={{ fontFamily: 'monospace' }}>
          {formatCurrencyFull(parseFloat(r.revenue || '0'))}
        </span>
      ),
    },
    {
      key: 'percent_of_total',
      label: '% of Total',
      width: 110,
      sortable: true,
      renderCell: (r) => (
        <span className="d-block text-end" style={{ fontFamily: 'monospace' }}>
          {r.percent_of_total ?? '0.00'}%
        </span>
      ),
    },
  ];

  return (
    <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
      <Card.Header className="bg-primary text-white flex-shrink-0">
        {headerPrefix}
        <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-bar-chart-line"></i>
            <span className="fw-semibold">Breakdown</span>
            <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
              <span className="text-primary fw-semibold">by</span>
              <Form.Select
                size="sm"
                value={dimension}
                onChange={(e) => onDimensionChange(e.target.value as BreakdownDimensionKey)}
                style={{ minWidth: '160px' }}
              >
                {dimensions.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </Form.Select>
            </div>
          </div>
          {summary && (
            <div className="d-flex align-items-center gap-3 small">
              <span><strong>{summary.row_count ?? 0}</strong> {dimensionOpt.label.toLowerCase()}s</span>
              <span><strong>{summary.total_event_count ?? 0}</strong> {(countLabel ?? 'Events').toLowerCase()}</span>
              <span><strong>{formatCurrencyFull(parseFloat(summary.total_revenue || '0'))}</strong> revenue</span>
            </div>
          )}
        </div>
      </Card.Header>
      <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
        {error && (
          <Alert variant="danger" className="m-3">
            Failed to load breakdown: {error.message}
          </Alert>
        )}
        <DataTable
          data={rows}
          columns={columns}
          getRowKey={(r) => r.label || ''}
          sortConfig={{ field: sortField, direction: sortAsc ? 'asc' : 'desc' }}
          onSortChange={(field, dir) => {
            setSortField(field as any);
            setSortAsc(dir === 'asc');
          }}
          pagination={{ hasMore, limit: FETCH_LIMIT }}
          onLoadMore={handleLoadMore}
          isLoading={isLoading}
          enableColumnReorder={true}
          enableColumnResize={true}
          enableColumnVisibility={true}
          enableStickyHeader={true}
          preferencesKey={`financial_breakdown_${dimension}`}
          userId={userId}
          userPreferences={userPreferences}
          maxHeight="100%"
          emptyState={<div className="text-center text-muted py-3">No events in scope.</div>}
        />
      </Card.Body>
    </Card>
  );
};

export default IncomeBreakdownSection;
